import {
  Activity,
  Moon,
  Heart,
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  Wifi,
  WifiOff,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useClientHealthSummary, ReadinessTrend } from '@/hooks/use-client-health-summary';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface ClientHealthOverviewProps {
  clientId: string;
  className?: string;
}

function getReadinessColor(score: number | null): string {
  if (score === null) return 'text-muted-foreground';
  if (score >= 80) return 'text-success';
  if (score >= 60) return 'text-warning';
  if (score >= 40) return 'text-warning';
  return 'text-destructive';
}

function getReadinessBgColor(score: number | null): string {
  if (score === null) return 'bg-muted';
  if (score >= 80) return 'bg-success/15';
  if (score >= 60) return 'bg-warning/15';
  if (score >= 40) return 'bg-warning/15';
  return 'bg-destructive/15';
}

function getTrendIcon(trend: ReadinessTrend) {
  switch (trend) {
    case 'improving':
      return <TrendingUp className="h-4 w-4 text-success" />;
    case 'declining':
      return <TrendingDown className="h-4 w-4 text-destructive" />;
    default:
      return <Minus className="h-4 w-4 text-muted-foreground" />;
  }
}

function StatCard({
  icon: Icon,
  label,
  value,
  subValue,
  iconColor,
  bgColor,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number | null;
  subValue?: string;
  iconColor?: string;
  bgColor?: string;
}) {
  return (
    <div className={cn('rounded-lg p-3', bgColor || 'bg-muted/50')}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={cn('h-4 w-4', iconColor || 'text-muted-foreground')} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <div className="text-lg font-semibold">{value ?? '—'}</div>
      {subValue && <div className="text-xs text-muted-foreground">{subValue}</div>}
    </div>
  );
}

export function ClientHealthOverview({ clientId, className }: ClientHealthOverviewProps) {
  const { summary, loading, error } = useClientHealthSummary(clientId);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show empty state if no health connection
  if (!summary?.hasHealthConnection) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-muted-foreground" />
            Health Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <WifiOff className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground mb-1">No health device connected</p>
            <p className="text-xs text-muted-foreground">
              Client needs to connect Apple Health, Health Connect, or Garmin
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const readinessColor = getReadinessColor(summary.avgReadiness7d);
  const readinessBg = getReadinessBgColor(summary.avgReadiness7d);

  return (
    <Card className={cn('border', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Health Overview
          </span>
          <div className="flex items-center gap-2">
            {summary.daysSinceSync && summary.daysSinceSync > 3 ? (
              <Badge variant="outline" className="text-warning border-warning/30 bg-warning/10">
                <WifiOff className="h-3 w-3 mr-1" />
                {summary.daysSinceSync}d since sync
              </Badge>
            ) : (
              <Badge variant="outline" className="text-success border-success/30 bg-success/10">
                <Wifi className="h-3 w-3 mr-1" />
                Synced
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Main readiness score */}
        <div
          className={cn(
            'flex items-center justify-between p-4 rounded-lg mb-4',
            readinessBg
          )}
        >
          <div>
            <div className="text-sm text-muted-foreground mb-1">7-Day Readiness</div>
            <div className={cn('text-3xl font-bold', readinessColor)}>
              {summary.avgReadiness7d ?? '—'}
              {summary.avgReadiness7d !== null && (
                <span className="text-lg font-normal">/100</span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-sm mb-1">
              {getTrendIcon(summary.readinessTrend)}
              <span className="capitalize text-muted-foreground">{summary.readinessTrend}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              30-day avg: {summary.avgReadiness30d ?? '—'}
            </div>
          </div>
        </div>

        {/* Metrics grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Moon}
            label="Sleep (7d avg)"
            value={summary.avgSleepHours7d ? `${summary.avgSleepHours7d}h` : null}
            iconColor="text-chart-4"
            bgColor="bg-chart-4/10"
          />
          <StatCard
            icon={Zap}
            label="HRV (7d avg)"
            value={summary.avgHrv7d ? `${summary.avgHrv7d}ms` : null}
            iconColor="text-success"
            bgColor="bg-success/10"
          />
          <StatCard
            icon={Heart}
            label="Resting HR"
            value={summary.avgRhr7d ? `${summary.avgRhr7d}bpm` : null}
            iconColor="text-chart-8"
            bgColor="bg-chart-8/10"
          />
          <StatCard
            icon={Clock}
            label="Last Contact"
            value={
              summary.daysSinceTouchpoint !== null
                ? summary.daysSinceTouchpoint === 0
                  ? 'Today'
                  : `${summary.daysSinceTouchpoint}d ago`
                : null
            }
            subValue={
              summary.touchpointsThisWeek
                ? `${summary.touchpointsThisWeek} this week`
                : undefined
            }
            iconColor="text-info"
            bgColor="bg-info/10"
          />
        </div>

        {/* Last sync info */}
        {summary.lastSyncAt && (
          <div className="mt-4 text-xs text-muted-foreground text-center">
            Last synced {formatDistanceToNow(new Date(summary.lastSyncAt), { addSuffix: true })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
